import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import { toast } from 'react-toastify';

const QueryTypes = [
  { value: 'mysql', label: 'MySQL' },
  { value: 'neo4j', label: 'Neo4j' },
  { value: 'mongodb', label: 'MongoDB' }
];

const NodeTypes = [
  { value: 'Party', label: 'Party' },
  { value: 'Account', label: 'Account' }
];

const ScheduleTypes = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

export default function AlertForm({ alert, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    query: '',
    query_type: 'mysql',
    node_type: 'Party',
    schedule_type: 'daily',
    is_active: true
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editorLanguage, setEditorLanguage] = useState('sql');
  const [isEditorReady, setIsEditorReady] = useState(false);

  useEffect(() => {
    if (alert) {
      setFormData({
        name: alert.name || '',
        description: alert.description || '',
        query: alert.query || '',
        query_type: alert.query_type || 'mysql',
        node_type: alert.node_type || 'Party',
        schedule_type: alert.schedule_type || 'daily',
        is_active: alert.is_active !== undefined ? alert.is_active : true
      });
      
      // Set initial editor language based on query type
      updateEditorLanguage(alert.query_type || 'mysql');
    }
  }, [alert]);
  
  // Function to update editor language based on query type
  const updateEditorLanguage = (queryType) => {
    switch(queryType) {
      case 'mysql':
        setEditorLanguage('sql');
        break;
      case 'neo4j':
        setEditorLanguage('cypher');
        break;
      case 'mongodb':
        setEditorLanguage('javascript');
        break;
      default:
        setEditorLanguage('sql');
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.query.trim()) newErrors.query = 'Query is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If query type changes, update editor language
    if (name === 'query_type') {
      updateEditorLanguage(value);
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleEditorChange = (value) => {
    setFormData({
      ...formData,
      query: value
    });
  };

  // Function to handle editor mounting
  const handleEditorDidMount = () => {
    setIsEditorReady(true);
  };

  // Function to handle editor error
  const handleEditorError = (error) => {
    console.error('Monaco Editor error:', error);
    toast.error('Error loading code editor. Please try refreshing the page.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      let response;
      
      if (alert && alert.id) {
        // Update existing alert
        response = await axios.put(`/api/alerts/${alert.id}`, formData);
        toast.success('Alert updated successfully!');
      } else {
        // Create new alert
        response = await axios.post('/api/alerts', formData);
        toast.success('Alert created successfully!');
      }
      
      if (onSave) onSave(response.data);
    } catch (error) {
      console.error('Error saving alert:', error);
      const errorMessage = error.response?.data?.detail || 'An error occurred while saving the alert';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white shadow p-6 rounded">
      <h2 className="bg-purple-l border-b border-gray-200 px-6 py-2 text-white font-bold tracking-wider uppercase text-md mb-5">
        {alert && alert.id ? 'Edit Alert' : 'Create New Alert'}
      </h2>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Alert Name
          </label>
          <input
            className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.name ? 'border-red-500' : ''}`}
            id="name"
            name="name"
            type="text"
            placeholder="Enter alert name"
            value={formData.name}
            onChange={handleChange}
          />
          {errors.name && <p className="text-red-500 text-xs italic">{errors.name}</p>}
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
            Description
          </label>
          <textarea
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            id="description"
            name="description"
            placeholder="Enter alert description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="query">
            Query
          </label>
          <div className="bg-purple-l p-2 text-xs flex items-center justify-between mb-5 rounded-t">
            <div>
              <span className="font-semibold">Language:</span> {formData.query_type === 'mysql' ? 'SQL' : 
               formData.query_type === 'neo4j' ? 'Cypher' : 
               'JavaScript'}
            </div>
          </div>
          <div className={`${errors.query ? 'border border-red-500 rounded-b' : 'rounded-b'}`}>
            {!isEditorReady && (
              <div className="flex justify-center items-center h-48 bg-gray-100">
                <p className="text-gray-500">Loading editor...</p>
              </div>
            )}
            <Editor
              height="200px"
              language={editorLanguage}
              theme="github"
              value={formData.query}
              onChange={handleEditorChange}
              onMount={handleEditorDidMount}
              onError={handleEditorError}
              loading={<div className="flex justify-center items-center h-48 bg-gray-100">
                <p className="text-gray-500">Loading editor...</p>
              </div>}
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 14,
                automaticLayout: true,
                lineNumbers: 'on',
                roundedSelection: true,
                cursorStyle: 'line',
                wordWrap: 'on',
                suggestOnTriggerCharacters: true,
                tabSize: 2
              }}
            />
          </div>
          <div className="mt-1 flex items-center">
            {errors.query && <p className="text-red-500 text-xs italic">{errors.query}</p>}
          </div>
        </div>
        
        <div className="flex flex-wrap -mx-2 mb-4">
          <div className="w-full md:w-1/3 px-2 mb-4 md:mb-0">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="query_type">
              Query Type
            </label>
            <select
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="query_type"
              name="query_type"
              value={formData.query_type}
              onChange={handleChange}
            >
              {QueryTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div className="w-full md:w-1/3 px-2 mb-4 md:mb-0">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="node_type">
              Node Type
            </label>
            <select
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="node_type"
              name="node_type"
              value={formData.node_type}
              onChange={handleChange}
            >
              {NodeTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div className="w-full md:w-1/3 px-2">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="schedule_type">
              Schedule Type
            </label>
            <select
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="schedule_type"
              name="schedule_type"
              value={formData.schedule_type}
              onChange={handleChange}
            >
              {ScheduleTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mb-6">
          <label className="flex items-center" htmlFor="is_active">
            <input
              type="checkbox"
              name="is_active"
              id="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="form-checkbox h-5 w-5 text-gray-700"
            />
            <span className="ml-2 text-gray-700 text-sm font-bold">Active</span>
          </label>
        </div>
        
        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="bg-purple-l hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Alert'}
          </button>
        </div>
      </form>
    </div>
  );
} 